import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Rocket, Building, Globe2 } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/constants";
import { SERVICES } from "@/data/services";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";
import { JobCard } from "@/components/site/JobCard";
import { Seo } from "@/components/site/Seo";

const STATS = [
  { value: "500+", label: "Placements delivered" },
  { value: "48 hrs", label: "Average first shortlist" },
  { value: "96%", label: "Offer acceptance rate" },
  { value: "8", label: "Industries covered" },
];

const CHAPTERS = [
  {
    n: "01",
    title: "People before pipelines",
    body: "We don't spray resumes into inboxes. Every candidate is interviewed, reference-checked, and matched on craft, culture, and ambition — before you ever see a profile.",
  },
  {
    n: "02",
    title: "Speed with precision",
    body: "A first shortlist in 48 hours, not four weeks. Our talent benches are pre-vetted and warmed up, so urgency never means compromise.",
  },
  {
    n: "03",
    title: "Partners, not vendors",
    body: "We embed with your team, learn your bar, and stay accountable after the offer letter. Replacement guarantees and honest counsel come standard.",
  },
];

const LIFECYCLE = [
  { step: "Hire", note: "Search, staff, or outsource — the right people, fast." },
  { step: "Payroll", note: "Accurate, on-time payroll that runs itself." },
  { step: "Compliance", note: "Statutory, PoSH and labour-law cover, end to end." },
  { step: "Training", note: "Hire-train-deploy and skill academies that stick." },
  { step: "Retain", note: "30/60/90-day check-ins that keep offers accepted." },
];

const STAGES = [
  {
    icon: Rocket,
    stage: "First hires",
    who: "Startups",
    body: "HR-in-a-box for young companies: founding-team search, first policies, payroll setup and an apprenticeship pipeline — without hiring an HR department.",
  },
  {
    icon: Building,
    stage: "Scaling fast",
    who: "Growth companies",
    body: "Twenty hires a quarter without dropping the bar. A dedicated pod, SLA-driven shortlists, and offer-to-joining management that protects your runway.",
  },
  {
    icon: Globe2,
    stage: "Operating at scale",
    who: "Enterprises & GCCs",
    body: "Capability-center buildouts, multi-city contract staffing, compliance audits and leadership mandates — one accountable partner across locations.",
  },
];


export default function Home() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="Samanvay HR — Staffing, Recruitment & HR Services in Hyderabad"
        description="Samanvay HR is a recruitment and HR partner for companies that refuse to hire average — permanent hiring, contract staffing, payroll, compliance and leadership search."
      />
      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[#FFF7F4] via-[#FAFAFB] to-[#FAFAFB]">
        <motion.div style={{ y: yBg }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="h-[115%] w-full object-cover opacity-[0.14]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFF7F4]/60 via-[#FAFAFB]/85 to-[#FAFAFB]" />
        </motion.div>
        <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-brand/10 blur-[140px] animate-pulse-slow" />

        <div className="relative max-w-7xl mx-auto px-6 pt-44 pb-32 w-full">
          <MaskLine delay={0.1}>
            <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-slate-500">
              <span className="h-px w-10 bg-brand" /> Staffing & HR Studio
            </span>
          </MaskLine>
          <h1 className="mt-8 font-display font-semibold tracking-tighter leading-[0.98] text-4xl sm:text-5xl lg:text-6xl text-slate-900">
            <MaskLine delay={0.25}>Great teams</MaskLine>
            <MaskLine delay={0.37}>aren&rsquo;t found.</MaskLine>
            <MaskLine delay={0.49}>
              <span className="font-editorial italic font-medium text-brand">They&rsquo;re forged.</span>
            </MaskLine>
          </h1>
          <FadeUp delay={0.7} className="mt-10 max-w-xl">
            <p className="text-base sm:text-lg text-slate-600 font-light leading-relaxed">
              Samanvay HR is a recruitment and partner for companies that refuse to hire average —
              permanent hiring, contract staffing, payroll, compliance and leadership search,
              with a 48-hour first shortlist.
            </p>
          </FadeUp>
          <FadeUp delay={0.85} className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to="/careers"
              data-testid="hero-find-role-btn"
              className="group flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
            >
              Find your next role
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-hire-btn"
              className="rounded-full border border-slate-300 bg-white/70 backdrop-blur-xl px-7 py-3.5 text-sm font-medium text-slate-700 transition-colors duration-300 hover:border-brand/60 hover:text-brand"
            >
              Hire with us
            </Link>
          </FadeUp>

          <FadeUp delay={1} className="mt-24 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200/80 rounded-2xl overflow-hidden border border-slate-200/80" >
            {STATS.map((s) => (
              <div key={s.label} className="bg-white/90 backdrop-blur p-6" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="font-display text-3xl font-semibold text-slate-900">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">{s.label}</p>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      <EditorialMarquee />

      {/* ---------------- MANIFESTO ---------------- */}
      <section className="py-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <SectionTag>The Manifesto</SectionTag>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
              Three beliefs we hire by.
            </h2>
          </FadeUp>
          <div className="mt-20 space-y-0">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.08}>
                <div className="grid grid-cols-12 gap-6 items-start border-t border-slate-200 py-14" data-testid={`manifesto-chapter-${c.n}`}>
                  <p className="col-span-12 sm:col-span-3 font-editorial italic text-7xl lg:text-8xl text-brand/90 leading-none">{c.n}</p>
                  <div className="col-span-12 sm:col-span-8 sm:col-start-5">
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{c.title}</h3>
                    <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{c.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- LIFECYCLE STRIP ---------------- */}
      <section className="border-t border-slate-200 py-24" data-testid="lifecycle-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag>Offer Letter to Exit Letter</SectionTag>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                One partner for the whole{" "}
                <span className="font-editorial italic font-medium text-brand">employee journey.</span>
              </h2>
            </div>
            <p className="max-w-sm text-sm text-slate-500 leading-relaxed">
              Most agencies vanish after the joining bonus. We stay for the payroll run,
              the compliance audit, and the day-one training too.
            </p>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {LIFECYCLE.map((l, i) => (
              <FadeUp key={l.step} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-shadow duration-300 hover:shadow-glow-sm" data-testid={`lifecycle-${l.step.toLowerCase()}`}>
                  <p className="font-editorial italic text-3xl text-brand/80">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-display text-xl font-semibold text-slate-900">{l.step}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{l.note}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- SERVICES ---------------- */}
      <section className="py-32 border-t border-slate-200" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag>What We Do</SectionTag>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                Everything we do.
              </h2>
            </div>
            <Link to="/services" data-testid="services-view-all-link" className="group flex items-center gap-2 text-sm text-slate-500 hover:text-brand transition-colors duration-200">
              How we engage <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.slug} delay={Math.min(i * 0.05, 0.3)}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-preview-${s.slug}`}
                  className="group flex h-full flex-col rounded-3xl border border-slate-200/80 bg-white p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-glow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 text-brand flex items-center justify-center border border-orange-200/50 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                    <s.icon size={22} />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{s.excerpt}</p>
                  <span className="mt-6 flex items-center gap-2 text-xs font-medium text-slate-400 transition-colors duration-300 group-hover:text-brand">
                    Learn more <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- WHO WE SERVE ---------------- */}
      <section className="py-32 border-t border-slate-200" data-testid="stages-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <SectionTag>Who We Serve</SectionTag>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05] max-w-2xl">
              Built for every stage of the climb.
            </h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {STAGES.map((s, i) => (
              <FadeUp key={s.stage} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200/80 bg-white p-10 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-glow-sm" data-testid={`stage-${s.who.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-orange-50 text-brand flex items-center justify-center border border-orange-200/50">
                      <s.icon size={22} />
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{s.who}</span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-slate-900">{s.stage}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED JOBS ---------------- */}
      <section className="py-32 border-t border-slate-200" data-testid="featured-jobs">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag>Live Roles</SectionTag>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                Open right now.
              </h2>
            </div>
            <Link to="/careers" data-testid="jobs-view-all-link" className="group flex items-center gap-2 text-sm text-slate-500 hover:text-brand transition-colors duration-200">
              Browse all roles <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {jobs.map((j, i) => (
              <JobCard key={j.id} job={j} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="relative py-40 border-t border-slate-200 overflow-hidden" data-testid="cta-section">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[720px] rounded-full bg-brand/10 blur-[140px]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-display text-4xl sm:text-6xl font-semibold tracking-tighter text-slate-900 leading-[1.02]">
              Ready to build the team that{" "}
              <span className="font-editorial italic font-medium text-brand">builds the company?</span>
            </h2>
            <p className="mt-8 text-slate-600 max-w-xl mx-auto leading-relaxed">
              Tell us the role — or the whole HR headache. We&rsquo;ll bring a shortlist
              you&rsquo;ll actually want to interview, in 48 hours.
            </p>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="cta-hire-btn"
                className="rounded-full bg-brand px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
              >
                Start a search
              </Link>
              <Link
                to="/careers"
                data-testid="cta-careers-btn"
                className="rounded-full border border-slate-300 bg-white/70 backdrop-blur-xl px-8 py-4 text-sm font-medium text-slate-700 transition-colors duration-300 hover:border-brand/60 hover:text-brand"
              >
                See open roles
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
